import { useEffect, useMemo, useState } from 'react';
import {
  fetchOrderFieldDisplayRules,
  type OrderFieldDisplayRule,
} from '../services/orderFieldDisplay.service';
import { toDbName } from './orderFieldDbNames';

// The camelCase -> legacy DB column mapping lives in its own leaf module so the
// Search Results edit gate can share it without importing this hook (and with it the
// API client). Re-exported here because callers already import it from this file.
export { ORDER_FIELD_DB_NAMES, toDbName } from './orderFieldDbNames';

export interface OrderFieldDisplay {
  /** True once the rules have loaded. Before then every predicate returns the
   *  safe default (nothing hidden / nothing forced-readonly = today's behavior). */
  ready: boolean;
  /** Is this field hidden for the current movetype/invoiced state? */
  isFieldHidden: (key: string) => boolean;
  /** Is this field forced read-only by the display layer? (Separate from the
   *  write-permission layer — combine with it: editable = mayWrite && !readonly.) */
  isFieldReadonly: (key: string) => boolean;
  /** Is this whole tab hidden for the current movetype? */
  isTabHidden: (tabId: string) => boolean;
  /**
   * Is this field RELEASED right now by an active admin override?
   *
   * True only when override is on AND a readonly rule for the field exists AND that rule
   * opted in via `overridable`. Distinct from `!isFieldReadonly`, which is also true when
   * no rule exists at all — including when the rules failed to load.
   *
   * Use this wherever a lock is enforced in CODE (the Invoicing line grid's `invoiced`
   * boolean, a paid leg) rather than by the config layer. Those locks must keep their own
   * floor: the rules fetch falls back to an empty list on error, so a lock that DEPENDED
   * on config would quietly open on a network blip. Asking "did an override release this"
   * instead fails closed — no rules, no release.
   */
  isFieldOverridden: (key: string) => boolean;
}

/**
 * Resolves the config-driven DISPLAY rules for an order into hide/readonly
 * predicates. Pass the order's EFFECTIVE movetype (honoring an unsaved edit) and
 * invoiced state so fields react live to a movetype change in the form.
 *
 * DISPLAY layer only — orthogonal to write-permission gating (order-lc-fields /
 * leg-editing). A field is truly editable when the user may write it AND it is
 * shown AND not read-only here.
 *
 * `opts.overrideActive` is Admin Override Mode (#33501) — the admin turned the per-pro
 * override on. It drops READONLY rules that opted in via `overridable`, and nothing
 * else: hidden rules and hidden tabs are untouched, because hiding says "this field is
 * meaningless for this movetype" rather than "this field is locked", and an override
 * that unhid `vessel` on a DOMESTIC order would fix nothing. Rules without an explicit
 * `overridable: 'Y'` stay locked, so a bypass is always something someone declared.
 *
 * Override here is the DISPLAY half only. The server re-resolves the caller's
 * `pro-admin-override` grant on every save that claims it (core/admin_override.py), so
 * opening a field in the browser is never what authorizes the write.
 */
export function useOrderFieldDisplay(
  moveType: string | null | undefined,
  isInvoiced: boolean = false,
  opts?: { system?: string; overrideActive?: boolean },
): OrderFieldDisplay {
  const system = opts?.system ?? 'cms';
  const overrideActive = opts?.overrideActive ?? false;
  const [rules, setRules] = useState<OrderFieldDisplayRule[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchOrderFieldDisplayRules(system).then((r) => {
      if (!cancelled) setRules(r);
    });
    return () => {
      cancelled = true;
    };
  }, [system]);

  const mt = String(moveType ?? '').trim().toUpperCase();

  return useMemo<OrderFieldDisplay>(() => {
    const scopes = new Set<string>(['ALL']);
    if (mt) scopes.add(mt);
    if (isInvoiced) scopes.add('INVOICED');

    const hiddenFields = new Set<string>();
    const readonlyFields = new Set<string>();
    const overriddenFields = new Set<string>();
    const hiddenTabs = new Set<string>();
    for (const r of rules ?? []) {
      if (r.system !== system) continue;
      if (!scopes.has(r.scope)) continue;
      if (r.targetType === 'tab') {
        if (r.behavior === 'hidden') hiddenTabs.add(r.target);
      } else if (r.behavior === 'hidden') {
        hiddenFields.add(r.target);
      } else if (r.behavior === 'readonly') {
        // Override releases only the rules that declared themselves releasable. A
        // missing `overridable` reads as 'N' — migrations are hand-applied, so the
        // client can run ahead of migration 449, and "I don't know" has to mean "no"
        // for a bypass flag.
        if (overrideActive && r.overridable === 'Y') {
          overriddenFields.add(r.target);
          continue;
        }
        readonlyFields.add(r.target);
      }
    }

    return {
      ready: rules !== null,
      isFieldHidden: (key: string) => hiddenFields.has(toDbName(key)),
      isFieldReadonly: (key: string) => readonlyFields.has(toDbName(key)),
      isTabHidden: (tabId: string) => hiddenTabs.has(tabId),
      isFieldOverridden: (key: string) => overriddenFields.has(toDbName(key)),
    };
  }, [rules, mt, isInvoiced, system, overrideActive]);
}
